import { localStateStore } from '@/utils/localStateStore';

type QueueTrack = {
  _id?: string;
  id?: string;
  title?: string;
  name?: string;
  artist?: string;
  isAnnouncement?: boolean;
};

type QueuePlaylist<TTrack extends QueueTrack> = {
  _id?: string;
  id?: string;
  songs?: TTrack[];
  playbackPolicy?: {
    shuffleEnabled?: boolean;
    noRepeatSameDay?: boolean;
    recentCooldownCount?: number;
    maxHistorySize?: number;
  };
};

type QueueHistoryEntry = {
  songId: string;
  playlistId?: string | null;
  playlistType?: string | null;
  timestamp: number;
  dateKey: string;
  name?: string | null;
  artist?: string | null;
};

const DEFAULT_POLICY = {
  shuffleEnabled: true,
  noRepeatSameDay: true,
  recentCooldownCount: 30,
  maxHistorySize: 5000
};

class PlaybackQueueEngine {
  private readonly storageKey: string;
  private readonly duplicateRecordWindowMs: number;
  private history: QueueHistoryEntry[];

  constructor(options: { storageKey?: string; duplicateRecordWindowMs?: number } = {}) {
    this.storageKey = options.storageKey || 'veeq_playback_queue_history_v1';
    this.duplicateRecordWindowMs = options.duplicateRecordWindowMs || 30000;
    this.history = this.loadHistory();
  }

  private loadHistory(): QueueHistoryEntry[] {
    try {
      const raw = localStateStore.getString(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveHistory(): void {
    try {
      localStateStore.setJson(this.storageKey, this.history);
    } catch {
      // Playback must never fail because history cannot be persisted.
    }
  }

  private normalizePolicy(playlist: QueuePlaylist<QueueTrack> = {}) {
    const source = playlist.playbackPolicy || {};
    const recentCooldownCount = Number(source.recentCooldownCount);
    const maxHistorySize = Number(source.maxHistorySize);

    return {
      ...DEFAULT_POLICY,
      ...source,
      shuffleEnabled: source.shuffleEnabled !== false,
      noRepeatSameDay: source.noRepeatSameDay !== false,
      recentCooldownCount: Number.isFinite(recentCooldownCount)
        ? recentCooldownCount
        : DEFAULT_POLICY.recentCooldownCount,
      maxHistorySize: Number.isFinite(maxHistorySize)
        ? maxHistorySize
        : DEFAULT_POLICY.maxHistorySize
    };
  }

  private getTrackId(track?: QueueTrack | null): string | null {
    return track?._id || track?.id || null;
  }

  private getDateKey(timestamp = Date.now()): string {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  recordPlay<TTrack extends QueueTrack>(
    track: TTrack | null | undefined,
    playlistId?: string | null,
    metadata: { playlist?: QueuePlaylist<TTrack>; playlistType?: string | null } = {}
  ): void {
    const songId = this.getTrackId(track);
    if (!songId || track?.isAnnouncement) return;

    const now = Date.now();
    const last = this.history[this.history.length - 1];
    if (
      last &&
      last.songId === songId &&
      last.playlistId === playlistId &&
      now - last.timestamp < this.duplicateRecordWindowMs
    ) {
      return;
    }

    const policy = this.normalizePolicy(metadata.playlist || {});
    this.history.push({
      songId,
      playlistId,
      playlistType: metadata.playlistType || null,
      timestamp: now,
      dateKey: this.getDateKey(now),
      name: track?.name || track?.title || null,
      artist: track?.artist || null
    });

    if (this.history.length > policy.maxHistorySize) {
      this.history = this.history.slice(-policy.maxHistorySize);
    }

    this.saveHistory();
  }

  private getLastPlayedAt(songId: string | null): number {
    if (!songId) return 0;

    for (let index = this.history.length - 1; index >= 0; index -= 1) {
      if (this.history[index].songId === songId) {
        return this.history[index].timestamp;
      }
    }

    return 0;
  }

  private getRecentSongIds(count: number): Set<string> {
    if (!count || count <= 0) return new Set();

    return new Set(
      this.history
        .slice(-count)
        .map(entry => entry.songId)
        .filter(Boolean)
    );
  }

  private wasPlayedToday(songId: string | null): boolean {
    if (!songId) return false;

    const today = this.getDateKey();
    return this.history.some(entry => entry.songId === songId && entry.dateKey === today);
  }

  private buildIndexOrder(total: number, currentIndex: number, direction: 'next' | 'prev'): number[] {
    const order: number[] = [];
    if (!total || total <= 0) return order;

    const step = direction === 'prev' ? -1 : 1;
    let index = currentIndex;

    for (let visited = 0; visited < total; visited += 1) {
      index = (index + step + total) % total;
      order.push(index);
    }

    return order;
  }

  private filterSongs<TTrack extends QueueTrack>(
    indexes: number[],
    songs: TTrack[],
    isTrackPlayable: (track: TTrack) => boolean
  ): Array<{ index: number; track: TTrack }> {
    return indexes
      .map(index => ({ index, track: songs[index] }))
      .filter(({ track }) => track && !track.isAnnouncement && isTrackPlayable(track));
  }

  private randomIndex(maxExclusive: number): number {
    if (maxExclusive <= 1) return 0;

    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      return values[0] % maxExclusive;
    }

    return Math.floor(Math.random() * maxExclusive);
  }

  private pickLeastRecentlyPlayed<TTrack extends QueueTrack>(
    candidates: Array<{ index: number; track: TTrack }>,
    avoidIndex: number | null = null
  ): { index: number; track: TTrack } | null {
    let oldest = Infinity;

    for (const candidate of candidates) {
      const lastPlayed = this.getLastPlayedAt(this.getTrackId(candidate.track));
      if (lastPlayed < oldest) {
        oldest = lastPlayed;
      }
    }

    let oldestCandidates = candidates.filter(
      candidate => this.getLastPlayedAt(this.getTrackId(candidate.track)) === oldest
    );

    if (oldestCandidates.length > 1 && avoidIndex !== null) {
      const nonOrderedCandidates = oldestCandidates.filter(candidate => candidate.index !== avoidIndex);
      if (nonOrderedCandidates.length > 0) {
        oldestCandidates = nonOrderedCandidates;
      }
    }

    return oldestCandidates[this.randomIndex(oldestCandidates.length)] || candidates[0] || null;
  }

  private selectBestSong<TTrack extends QueueTrack>(
    candidates: Array<{ index: number; track: TTrack }>,
    policy: typeof DEFAULT_POLICY,
    orderedFirstIndex: number | null = null
  ): { index: number; track: TTrack } | null {
    if (candidates.length === 0) return null;

    const recentIds = this.getRecentSongIds(policy.recentCooldownCount);
    const strict = candidates.filter(({ track }) => {
      const songId = this.getTrackId(track);
      return songId && (!policy.noRepeatSameDay || !this.wasPlayedToday(songId)) && !recentIds.has(songId);
    });

    if (strict.length > 0) {
      return policy.shuffleEnabled ? this.pickLeastRecentlyPlayed(strict, orderedFirstIndex) : strict[0];
    }

    const notRecent = candidates.filter(({ track }) => {
      const songId = this.getTrackId(track);
      return songId && !recentIds.has(songId);
    });

    if (notRecent.length > 0) {
      return policy.shuffleEnabled ? this.pickLeastRecentlyPlayed(notRecent, orderedFirstIndex) : notRecent[0];
    }

    return policy.shuffleEnabled ? this.pickLeastRecentlyPlayed(candidates, orderedFirstIndex) : candidates[0];
  }

  selectNext<TTrack extends QueueTrack>({
    playlist,
    currentIndex = -1,
    currentTrackId = null,
    direction = 'next',
    isTrackPlayable = () => true
  }: {
    playlist?: QueuePlaylist<TTrack> | null;
    currentIndex?: number;
    currentTrackId?: string | null;
    direction?: 'next' | 'prev';
    isTrackPlayable?: (track: TTrack) => boolean;
  }): { index: number; track: TTrack; reason: string } | null {
    const songs = Array.isArray(playlist?.songs) ? playlist.songs : [];
    if (songs.length === 0) return null;

    const policy = this.normalizePolicy(playlist || {});
    const order = this.buildIndexOrder(songs.length, currentIndex, direction);
    if (order.length === 0) return null;

    const immediate = songs[order[0]];
    if (immediate?.isAnnouncement && isTrackPlayable(immediate)) {
      return { index: order[0], track: immediate, reason: 'announcement' };
    }

    let candidates = this.filterSongs(order, songs, isTrackPlayable);
    const activeTrackId = currentTrackId || this.getTrackId(songs[currentIndex]);

    if (activeTrackId) {
      candidates = candidates.filter(({ track }) => this.getTrackId(track) !== activeTrackId);
    }

    const selected = this.selectBestSong(candidates, policy, candidates[0]?.index ?? null);
    return selected ? { ...selected, reason: policy.shuffleEnabled ? 'smart-shuffle' : 'ordered' } : null;
  }
}

export default PlaybackQueueEngine;
